import React from 'react';
import { Select } from 'antd';
// import { CalendarOutlined, ExclamationCircleOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styles from './StatusSelector.module.scss';
import { getStatusColor } from '../../utils/statusHelpers';

const { Option } = Select;

interface StatusSelectorProps {
  className?: string;
  onChange: (status: string) => void;
  disabled: boolean;
  userRole?: string;
  selectedItems?: Set<number>;
  selectedItemsDetails?: Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any }>;
  fileDetail?: any;
  currentEntry?: any;
  selectedVersion?: number;
  canMultiSelect: () => boolean;
}

const StatusSelector: React.FC<StatusSelectorProps> = ({
  className,
  onChange,
  disabled,
  userRole,
  selectedItems,
  selectedItemsDetails,
  fileDetail,
  currentEntry,
  selectedVersion,
  canMultiSelect
}) => {
  // Get current status
  const getCurrentStatus = () => {
    if (selectedItems && selectedItems.size > 0) {
      // Get status from first selected item
      const firstSelectedItem = selectedItemsDetails?.[0];
      return firstSelectedItem?.status || 'NeedsReview';
    } else if (fileDetail) {
      // Get status from selected version or file
      if (selectedVersion && fileDetail.versions) {
        const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData) {
          return selectedVersionData.status || fileDetail.status || 'NeedsReview';
        }
      }
      return fileDetail.status || 'NeedsReview';
    } else if (currentEntry) {
      return currentEntry.status || 'NeedsReview';
    }
    return 'NeedsReview';
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'NeedsReview':
        return 'Needs Review';
      case 'Updated':
        return 'Updated';
      case 'Approved':
        return 'Approved';
      default:
        return status;
    }
  };

  const renderStatusOption = (status: string, label: string) => (
    <span className={styles.statusOption}>
      <span className={styles.statusDot} style={{ backgroundColor: getStatusColor(status) }} />
      <span className={styles.statusLabel}>{label}</span>
    </span>
  );

  // Get status options based on user role
  const getStatusOptions = () => {
    const currentStatus = getCurrentStatus();
    if (!userRole) return [] as Array<{ value: string; label: string; disabled: boolean }>;
    
    // All status options from enum
    const all = ['NeedsReview', 'Updated', 'Approved'];
    const allStatusOptions = all.map((value) => ({
      value,
      label: getStatusLabel(value),
      disabled: false
    }));
    
    if (['AccountOwner', 'SuperAdmin'].includes(userRole)) {
      return allStatusOptions;
    }
    
    if (userRole === 'InternalTeam') {
      return [
        { value: 'NeedsReview', label: 'Needs Review', disabled: false }
      ];
    }
    
    if (userRole === 'BrandAccountViewer') {
      if (currentStatus !== 'NeedsReview') {
        return [
          { value: currentStatus, label: `${getStatusLabel(currentStatus)} (View Only)`, disabled: true }
        ];
      }
      return [
        { value: 'Updated', label: 'Updated', disabled: false },
        { value: 'Approved', label: 'Approved', disabled: false }
      ];
    }
    
    return [
      { value: currentStatus, label: `${getStatusLabel(currentStatus)} (Current)`, disabled: false }
    ];
  };

  // Check if user can change status
  const canChangeStatus = () => {
    if (!userRole) return false;
    
    if (['AccountOwner', 'SuperAdmin'].includes(userRole)) {
      return true;
    }
    
    if (['BrandAccountViewer', 'InternalTeam'].includes(userRole)) {
      const currentPath = window.location.pathname;
      return currentPath.includes('/versions') || fileDetail !== null || fileDetail !== undefined;
    }
    
    return false;
  };

  const currentStatus = getCurrentStatus();
  const isDisabled = disabled || 
    !canChangeStatus() || 
    (selectedItems && selectedItems.size === 0) || 
    (selectedItems && selectedItems.size > 1 && !canMultiSelect());

  // Should show current status value (only in FileDetail page with selected version)
  const shouldShowValue = () => {
    return !!(fileDetail && selectedVersion);
  };

  // Guest view
  if (userRole === 'Guest') {
    return (
      <div className={styles.statusContainer}>
        <div className={styles.statusDisplay}>
          <div className={styles.statusDisplayContent}>
            <span className={styles.statusLabel}>{getStatusLabel(currentStatus)}</span>
            <div 
              className={styles.statusIndicator}
              style={{ backgroundColor: getStatusColor(currentStatus) }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Brand/Agency Projects page view only
  if (['BrandAccountViewer', 'InternalTeam'].includes(userRole || '')) {
    const currentPath = window.location.pathname;
    const isProjectsPage =
      currentPath === '/internal-projects' ||
      currentPath === '/projects' ||
      currentPath === '/external-projects' ||
      (!currentPath.includes('/versions') && !fileDetail);
    
    if (isProjectsPage) {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusDisplay}>
            <div className={styles.statusDisplayContent}>
              <span className={styles.statusLabel}>{getStatusLabel(currentStatus)}</span>
              <div 
                className={styles.statusIndicator}
                style={{ backgroundColor: getStatusColor(currentStatus) }}
              />
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className={styles.statusContainer}>
      <Select
        placeholder="Status"
        value={shouldShowValue() ? getCurrentStatus() : undefined}
        onChange={onChange}
        className={styles.statusSelect}
                    popupClassName={styles.statusDropdown}
        disabled={isDisabled}
      >
        {getStatusOptions().map((option) => (
          <Option key={option.value} value={option.value} disabled={option.disabled}>
            {renderStatusOption(option.value, option.label)}
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default StatusSelector; 